import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ModalContext } from "apps/portal/src/providers";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EditIcon } from "../..";
import { MenuPopover, MenuPopoverOptionType } from "../../base";
import { Button } from "../../Button";
import { CancelIcon } from "../../icons/CancelIcon";
import { Coins03Icon } from "../../icons/Coins03Icon";
import { CreditPackItemType } from "../CreditPackItem";
import { AdjustQuantityModal } from "./AdjustQuantityModal";
import { EditExpiryDateModal } from "./EditExpiryDateModal";
import { ExpireCreditPackModal } from "./ExpireCreditPackModal";

type CreditPackMenuPopoverProps = {
  hideActions: boolean;
  userMemberId: string;
  id: number;
  creditPack: CreditPackItemType;
};

export const CreditPackMenuPopover = ({
  hideActions,
  userMemberId,
  id,
  creditPack: {
    sessionsIncluded,
    sessionsUnlimited,
    status,
    sessionsLeft,
    partOfMembership,
    expiryDate,
  },
}: CreditPackMenuPopoverProps) => {
  const { setModal, hide: hideModal } = useContext(ModalContext);
  const { t } = useTranslation();

  const menuOptions = useMemo(() => {
    if (hideActions) {
      return [];
    }

    const baseMenuOptions = [
      {
        id: "adjustQuantity",
        label: t("components.creditPackItem.menuOptions.adjustQuantity"),
        icon: (
          <Coins03Icon
            className="size-5"
            pathClassName="stroke-gray-500 dark:stroke-gray-400"
          />
        ),
        onClick: () => {
          setModal(
            <AdjustQuantityModal
              hide={() => hideModal()}
              totalCredits={sessionsIncluded}
              remaining={sessionsLeft}
              subscriptionId={id}
              userMemberId={userMemberId}
            />,
          );
        },
      },
      {
        id: "editExpiryDate",
        label: t("components.creditPackItem.menuOptions.editExpiryDate"),
        icon: (
          <EditIcon
            className="size-5"
            pathClassName="stroke-gray-500 dark:stroke-gray-400"
          />
        ),
        onClick: () => {
          setModal(
            <EditExpiryDateModal
              hide={() => hideModal()}
              initialDate={expiryDate}
              subscriptionId={id}
              userMemberId={userMemberId}
            />,
          );
        },
      },
      {
        id: "expire",
        label: t("components.creditPackItem.menuOptions.expire"),
        classNameLabel: "text-error-600",
        icon: <CancelIcon className="size-5" />,
        topDivider: true,
        onClick: () => {
          setModal(
            <ExpireCreditPackModal
              subscriptionId={id}
              userMemberId={userMemberId}
              hide={() => hideModal()}
            />,
          );
        },
      },
    ] satisfies MenuPopoverOptionType[];

    if (status !== "ACTIVE") {
      return [];
    }

    return baseMenuOptions.filter(({ id }) => {
      if (id === "adjustQuantity" && sessionsUnlimited) {
        return false;
      }
      if (id === "editExpiryDate" && partOfMembership) {
        return false;
      }
      if (id === "expire" && partOfMembership) {
        return false;
      }
      return true;
    });
  }, [
    hideActions,
    t,
    status,
    setModal,
    sessionsIncluded,
    sessionsLeft,
    id,
    userMemberId,
    hideModal,
    expiryDate,
    sessionsUnlimited,
    partOfMembership,
  ]);

  return (
    <MenuPopover
      classNameContent="w-[14rem]"
      hidden={menuOptions.length === 0}
      triggerButton={
        <Button className="size-9 p-0">
          <FontAwesomeIcon
            icon={faEllipsisV}
            className="dark:text-gray-25 size-4 text-gray-950"
          />
        </Button>
      }
      side="bottom"
      options={menuOptions}
    />
  );
};
